import { TurnstileInstance } from '@marsidev/react-turnstile';
import { Stack, Typography, Link, Button, FormHelperText } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { GoogleIcon } from '@/assets/icons/social/GoogleIcon';
import { PublicRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { storePendingOAuthCampaign, clearPendingOAuthCampaign } from '@/localStorage/campaign';

import ResetPasswordModal from '@/components/auth/ResetPasswordModal';
import FormCaptcha from '@/components/form/FormCaptcha';
import FormPassword from '@/components/form/FormPassword';
import FormTextField from '@/components/form/FormTextField';

import AuthPageWrapper from './AuthPageWrapper';

interface FormData {
  email: string;
  password: string;
  captchaToken: string;
}

const LogInPage: React.FC = () => {
  const { signInWithPassword, signInWithOAuth } = useAuth();
  const turnstileRef = useRef<TurnstileInstance>();
  const location = useLocation();

  // Extract all referral parameters from query
  const urlParams = new URLSearchParams(location.search);
  const campaign = urlParams.get('campaign');
  const redirect = urlParams.get('redirect');
  const referralType = urlParams.get('referral_type');
  const referralId = urlParams.get('referral_id');
  const referralUrl = urlParams.get('referral_url');

  // Restore shareRedirectUrl if passed via redirect parameter
  React.useEffect(() => {
    if (redirect) {
      localStorage.setItem('shareRedirectUrl', redirect);
    }
  }, [redirect]);

  // Create signup URL with all parameters including referral info
  let signupUrl: string = PublicRoutes.signup;
  const signupParams = new URLSearchParams();
  
  if (campaign) signupParams.append('campaign', campaign);
  if (referralType) signupParams.append('referral_type', referralType);
  if (referralId) signupParams.append('referral_id', referralId);
  if (referralUrl) signupParams.append('referral_url', referralUrl);
  
  // Pass along redirect parameter and any other metadata
  if (redirect) signupParams.append('redirect', redirect);
  urlParams.forEach((value, key) => {
    if (!['campaign', 'redirect', 'referral_type', 'referral_id', 'referral_url'].includes(key)) {
      signupParams.append(key, value);
    }
  });
  
  if (signupParams.toString()) {
    signupUrl = `${PublicRoutes.signup}?${signupParams.toString()}`;
  }

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();
  const [formError, setFormError] = useState<Error | null>(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

  const onSignInWithGoogle = async () => {
    setFormError(null);

    try {
      // Store campaign in localStorage before OAuth flow
      if (campaign) {
        storePendingOAuthCampaign(campaign);
      }

      // Prepare user metadata including campaign if present
      const userMetadata: Record<string, unknown> = {};
      if (campaign) {
        userMetadata.campaign = campaign;
      }

      const { error } = await signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.href,
          ...(Object.keys(userMetadata).length > 0 && { data: userMetadata }),
        },
      });

      if (error) throw error;
    } catch (error) {
      setFormError(error);
      // Clean up localStorage on error
      clearPendingOAuthCampaign();
    }
  };

  const onSignInWithPassword = async (data: FormData) => {
    const { email, password, captchaToken } = data;
    setFormError(null);

    try {
      const { error } = await signInWithPassword({
        email,
        password,
        options: {
          captchaToken,
        },
      });

      if (error) throw error;
    } catch (error) {
      setFormError(error);
      turnstileRef.current?.reset();
    }
  };

  return (
    <>
      <AuthPageWrapper>
        <Stack
          component="form"
          onSubmit={handleSubmit(onSignInWithPassword)}
          spacing={3}
          width="100%"
        >
          <Stack spacing={1.5} mb={1}>
            <Typography variant="h3">Log in</Typography>

            <Typography variant="body2">Welcome back! Please enter your details.</Typography>
          </Stack>

          <Stack spacing={2.5}>
            <FormTextField
              label="Email"
              placeholder="Enter your email"
              textFieldProps={{
                autoComplete: 'email',
                autoFocus: true,
                type: 'email',
              }}
              error={!!errors.email}
              helperText={errors.email?.message?.toString()}
              control={control}
              required
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email address',
                },
              })}
            />

            <Stack spacing={1}>
              <FormPassword
                label="Password"
                placeholder="Enter your password"
                textFieldProps={{
                  autoComplete: 'password',
                }}
                error={!!errors.password}
                helperText={errors.password?.message?.toString()}
                control={control}
                required
                {...register('password', {
                  required: 'Password is required',
                })}
              />

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  onClick={() => setIsResetPasswordModalOpen(true)}
                  sx={{
                    fontSize: 'body3.fontSize',
                    fontWeight: 'body3.fontWeight',
                    lineHeight: 'body3.lineHeight',
                    textTransform: 'none',
                    padding: 0,
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot password?
                </Button>
              </Stack>
            </Stack>

            <FormCaptcha
              control={control}
              error={!!errors.captchaToken}
              helperText={errors.captchaToken?.message?.toString()}
              required
              {...register('captchaToken', {
                required: 'Captcha Token is required',
              })}
              turnstileRef={turnstileRef}
            />

            {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
          </Stack>

          <Stack spacing={1} mb={1}>
            <LoadingButton loading={isSubmitting} fullWidth variant="contained" type="submit">
              Log in
            </LoadingButton>

            <Button
              fullWidth
              color="secondary"
              variant="outlined"
              onClick={onSignInWithGoogle}
              startIcon={<GoogleIcon />}
            >
              Log in with Google
            </Button>
          </Stack>

          <Typography variant="body3" textAlign="center">
            Don't have an account? <Link href={signupUrl}>Sign up</Link>
          </Typography>
        </Stack>
      </AuthPageWrapper>

      <ResetPasswordModal
        isOpen={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
      />
    </>
  );
};

export default LogInPage;
